import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import styled from "styled-components";
import { API, Book, BookAPI, BookmarkAPI, Endpoints, Project, Team, TeamAPI } from "@brg/api";
import { Colors, FInter, useToasts } from "@brg/ui";
import { useProfile } from "@brg/auth";
import { LikesBox } from "shared/components/likesBox";
import { ProjectDesignerName } from "shared/components/projectDesignerName";
import { CreateLinkTo } from "shared/constants/linkTo";
import { getBookImages } from "_deziiign/books/pages/sections/ps-image";
import { BookDelegate } from "../../book-delegate";
import { IBookLight } from "../../common";
import BookCardLayout from "./bookCardLayout";
import { BookCardTools } from "./bookCardTools";
import { AppTester } from "supporting";
import { ProjectCardTitle } from "shared/components/projectCard/styles";
import { BookmarkButton } from "shared/components/bookmarkButtonOnProject/BookmarkButton";
import { SelectRing } from "shared/components/projectCard/projectCardNew";
import { RingPositions } from "shared/constants/ringPositionType";
import { PointBox } from "../../../../../shared/components/pointBox/PointBox";
import RepostButton from "shared/components/repostButton/repostButton";
import { useDispatch, useSelector } from "react-redux";
import { ProjectsGridActions } from "shared/reducers/ProjectsGridSlice";
import { DesignerTab } from "shared/constants/designerTab";
import { HiddenContentText } from "shared/components/projectCard/components/HiddenContentText";
import { AlertBlock } from "shared/icons/AlertBlock";
import CheckSmallIcon from "shared/icons/CheckSmallIcon";

const Author = styled(FInter.SmallText)`
  color: ${Colors.geoWhite100};
  @media (max-width: 940px) {
  color: unset;
    div {
      font-weight: 400;
    }
  }
`

export interface BookCardProps extends Partial<IBookLight>/*, BookEvents*/ {
  isLoading?: boolean;
  width?: string;
  height?: string;
  onShouldReload?: (sender: any) => void;
  book?: Book;
  rank?: number;
  metaAlwaysVisible?: boolean;
  dataKey?: any;
  isSelectable?: boolean;
  selected?: boolean;
  alreadyProject?: boolean;
  linkPrefix?: string;
  isCanNotParticipate?: boolean;
  itemType?: string;
  channel?: Team<'Global'>;
  isChannel?: boolean;
  isCanNotBeDraft?: boolean;
  isSelectRightNeed?: boolean;
  isSortedByNotModerated?: boolean;
  withoutRepost?: boolean;
  isRepostHidden?: boolean,
  isBookmarkHidden?: boolean,
  isCanNotBeBookmark?: boolean;
  isParticipant?: boolean;
  onNotParticipate?: (projectId: string) => void;
  onBookMoveDrafts?: (identifier: string) => void
  onUpdate?: () => void;
  onSelect?: (projectId: string) => void;
  currentCompetitionId?: string;
  showCompetitionPoint?: boolean;
  showCompetitionJuryPoint?: boolean;
  showFirstCompetitionPoint?: boolean;
  entityType?: string
  isMobile?: boolean;
  linkTo?: string;
  linkTarget?: '_self' | '_blank';
}

const BookCard: FC<BookCardProps> = (props) => {
    const {
      isLoading,
      identifier,
      title,
      likes,
      author,
      coAuthors,
      isLiked,
      isSelectable,
      selected,
      alreadyProject,
      width,
      height,
      rank,
      onShouldReload,
      onSelect,
      metaAlwaysVisible,
      isCanNotBeDraft,
      withoutRepost,
      isSelectRightNeed,
      book,
      channel,
      linkPrefix,
      isCanNotParticipate,
      itemType,
      isChannel,
      isParticipant,
      onNotParticipate,
      onBookMoveDrafts,
      onUpdate,
      entityType,
      isRepostHidden,
      isBookmarkHidden,
      currentCompetitionId,
      showCompetitionPoint,
      showCompetitionJuryPoint,
      showFirstCompetitionPoint,
      isSortedByNotModerated,
      isMobile,
      linkTo,
      linkTarget,
    } = props;

    const isInCompetition = useMemo(() =>
      //@ts-ignore
      (Boolean(book && !book?.canEdit_PartCompetition)), [book])

    const history = useHistory();
    const profile = useProfile();
    const toasts = useToasts();
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string}>();
    const { t, i18n } = useTranslation();
    const isOnlines = AppTester.isOnlines();
    const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const [isBookmarked, setIsBookmarked] = useState<boolean>(false);

    const isFeedPage = useRouteMatch('/feed');

    const titleImages = getBookImages(book, i18n.language);

    //@ts-ignore
    const currentTeam = useSelector((state) => state?.organizations)?.team;

    const isFeedOwner = useMemo(
      () => isFeedPage || (entityType === DesignerTab.subscriptionsProjects && 
        id && (id === profile?.nickName || id === profile?.accountId))
    , [isFeedPage, entityType, id, profile])

    const handleToolTipToggleMove = (val: boolean) => {
      setTooltipOpen && setTooltipOpen(val);
    }

    const onDesignerSelected = useCallback((designerId: string): void => {
      history.push(CreateLinkTo.designer(designerId));
    }, [history]);

    const hidePrivateProject = useMemo(() => {
      return isChannel && !isParticipant && book?.status === 10
    } ,[isChannel, isParticipant, book])

    const onDraft = useCallback((bookId: string): void => {

      if (isInCompetition) {
        toasts.error(t('toasts.project_part_competition'));
        return;
      }
      
      if (onBookMoveDrafts) {
        onBookMoveDrafts(bookId)
        toasts.info(t("toasts.book_move_to_drafts_title", {
          title
        }));
        return
      }

      const prompt = window.confirm(t('book_edit.confirmation_msg.to_drafts'));

      if (!prompt) return

      BookAPI.UpdateStatus({ bookStatus: 0, bookId }).then(() => {
        onShouldReload?.(bookId);
        toasts.info(t("toasts.book_move_to_drafts_title", {
          title
        }));
      });
    }, [toasts, onShouldReload, title, isInCompetition]);

    const handleBookmarkClick = useCallback(() => {
      if (!book) return false;
      const method = isBookmarked ? 'RemoveBookmark' : 'Bookmark';
      BookmarkAPI[method]({ entityId: book.bookId, entityType: "project" })
        .then(() => {
          toasts.info(t(`book_edit.frames.book_bookmark_${method.toLowerCase()}`));
          setIsBookmarked(!isBookmarked);
          onUpdate && onUpdate();
          return;
        })
        .catch(() => {
          toasts.error(t('NEED_AUTHORIZATION'));
        })
      }, [t, book, toasts, isBookmarked]);

      useEffect(() => {
        if (!book) return
        //@ts-ignore
        setIsBookmarked(book.isBookmark);
      }, []);

    const isRejected = useMemo(() => BookDelegate.isRejectedBook(book), [book]);

    const backgroundStyles = useMemo(() => {
      if (hidePrivateProject) {
        return {
          background: 'linear-gradient( 180deg,rgba(0, 0, 0, 0),  rgba(24, 26, 28, 0.3), rgba(24, 26, 28, 1) )',
          backdropFilter: 'blur(25px)',
          marginTop: 0
        }
      }
      return undefined
    } , [hidePrivateProject])

    const backgroundText = useMemo(() => {
      if (hidePrivateProject) {
        return <HiddenContentText />
      }
      return undefined
    } , [hidePrivateProject])

    const onEdit = useCallback((bookId: string): void => {
      if (isInCompetition) {
        toasts.error(t('toasts.project_part_competition'));
        return;
      }
      history.push((linkPrefix ? linkPrefix : "") + CreateLinkTo.bookEdit(bookId))
    }, [history, linkPrefix]);

    const canBeDraft = useMemo(
      () => !isCanNotBeDraft && BookDelegate.canBeDraft(profile, props.book) && !BookDelegate.isPaid(props.book),
      [profile, props.book, isCanNotBeDraft]
    );

    const canEdit = useMemo(
      () => BookDelegate.canEdit(profile, props.book) && !BookDelegate.isPaid(props.book),
      [profile, props.book]
    );

  const competitionGPA = useMemo(() => {
    if (showCompetitionPoint && !isOnlines && currentCompetitionId && book) {
      if (showCompetitionJuryPoint) {
        //@ts-ignore
        return book.competitionGPA;
      }
      //@ts-ignore
      const points = book.competitonsGPA?.find(x => x.competitionId === currentCompetitionId);

      if (points) {
        return points.competitionGPA;
      }

      return undefined;
    }
    else if (book && showFirstCompetitionPoint) {
      //@ts-ignore
      return book.competitonsGPA?.[0]?.competitionGPA;
    }
    else {
      return undefined;
    }
  }, [showCompetitionPoint, showCompetitionJuryPoint, isOnlines, currentCompetitionId, book, showFirstCompetitionPoint]);
  const selectCheckIcon = useMemo(() => (
    <div>
      <CheckSmallIcon />
    </div>
  ), []);

  const selectRing = useMemo(() => {
    if (!isSortedByNotModerated) return undefined;
    return (
      <SelectRing className='selectRing' active={selected} ringPosition={RingPositions.left} onClick={(ev) => {
        if (onSelect) {
          ev.stopPropagation();
          ev.preventDefault();
          onSelect(identifier ?? '');
        }
      }}>
        {selected && selectCheckIcon}
      </SelectRing>
    );
  }, [identifier, isSortedByNotModerated, onSelect, selected, selectCheckIcon]);

  if(hidePrivateProject) {
    return (
      <BookCardLayout 
        book={book}
        isBook={book?.isBook}
        identifier={identifier}
        titleImages={titleImages}
        isLoading={isLoading}
        width={width}
        height={height}
        itemType={itemType}
        author={undefined}
        withoutGoToProject
        backgroundStyles={backgroundStyles}
        backgroundText={backgroundText}
        status={isRejected ? <AlertBlock /> : undefined}
        isRejected={isRejected}
        isMobile={isMobile}
      />
    )
  }

    return (
      <BookCardLayout
        book={book}
        linkParams={showCompetitionJuryPoint && currentCompetitionId ? `?currentCompetitionId=${currentCompetitionId}` : ''}
        identifier={identifier}
        titleImages={titleImages}
        isLoading={isLoading}
        isMobile={isMobile}
        linkTo={linkTo}
        linkTarget={linkTarget}
        // @ts-ignore
        isBook={book?.isBook}
        width={width}
        height={height}
        rank={rank}
        metaAlwaysVisible={metaAlwaysVisible}
        title={<ProjectCardTitle>{title}</ProjectCardTitle>}
        isMoreVisible={canBeDraft || canEdit || isSelectable}
        tooltipOpen={isTooltipOpen}
        toggleTooltip={handleToolTipToggleMove}
        linkPrefix={linkPrefix ? linkPrefix : undefined }
        itemType={itemType}
        isRepostHidden={isRepostHidden}
        isBookMarkHidden={isBookmarkHidden}
        isSelectRightNeed={isSelectRightNeed || isSortedByNotModerated}
        bookmarkButton={
          (!isSelectable && <BookmarkButton isBookmarked={isBookmarked} onClick={handleBookmarkClick}/>)
        }
        status={isRejected ? <AlertBlock /> : undefined}
        isRejected={isRejected}
        selectButton={
          isSortedByNotModerated
            ? selectRing
            : (alreadyProject || isSelectable) && (
              <>
              {alreadyProject ? (
              <SelectRing active={selected}>
                  {selectCheckIcon}
              </SelectRing>
            ) : isSelectable
              ? (
                <SelectRing active={selected} onClick={(ev) => {
                  if (onSelect) {
                    ev.stopPropagation();
                    ev.preventDefault();
                    onSelect(identifier ?? '');
                  }
                }}>
                  {selected && selectCheckIcon}
                </SelectRing>) : false}
          
            </>
            )
        }
        more={(
          (!isSelectable && (
            <BookCardTools
              isCanBeDraft={canBeDraft}
              isEditable={canEdit}
              identifier={identifier}
              book={book}
              onBookMoveDrafts={onDraft}
              onBookEdit={onEdit}
              isCanNotParticipate={isCanNotParticipate}
              onNotParticipate={onNotParticipate}
            />
          ))
        )}
        author={
          <Author>
            <ProjectDesignerName
              value={author ? [author].concat(coAuthors ?? []) : (coAuthors ?? [])}
              onSelected={onDesignerSelected}
              linkTarget={linkTarget === "_blank" ? "_blank" : undefined}
            />
          </Author>
        }
        repostButton={
          !isSelectable && !withoutRepost && profile && (
            <RepostButton
              project={book as unknown as Project} 
              iconOnPage 
              onUpdate={onShouldReload}
            />
          )
        }
        point={
          ((showFirstCompetitionPoint || showCompetitionPoint) && !isOnlines) && <PointBox points={competitionGPA}/>
        }
        likes={
          !isOnlines && (
            <LikesBox likes={likes ?? 0} isLiked={isLiked} isInteractive onClick={() => history.push(CreateLinkTo.project(identifier ?? ''))}/>
          )
        }
      />
    );
  }
;

export default memo(BookCard);
import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import styled from "styled-components";
import { API, Book, BookAPI, BookmarkAPI, Endpoints, Project, Team, TeamAPI } from "@brg/api";
import { Colors, FInter, useToasts } from "@brg/ui";
import { useProfile } from "@brg/auth";
import { LikesBox } from "shared/components/likesBox";
import { ProjectDesignerName } from "shared/components/projectDesignerName";
import { CreateLinkTo } from "shared/constants/linkTo";
import { getBookImages } from "_deziiign/books/pages/sections/ps-image";
import { BookDelegate } from "../../book-delegate";
import { IBookLight } from "../../common";
import BookCardLayout from "./bookCardLayout";
import { BookCardTools } from "./bookCardTools";
import { AppTester } from "supporting";
import { ProjectCardTitle } from "shared/components/projectCard/styles";
import { BookmarkButton } from "shared/components/bookmarkButtonOnProject/BookmarkButton";
import { SelectRing } from "shared/components/projectCard/projectCardNew";
import { RingPositions } from "shared/constants/ringPositionType";
import { PointBox } from "../../../../../shared/components/pointBox/PointBox";
import RepostButton from "shared/components/repostButton/repostButton";
import { useDispatch, useSelector } from "react-redux";
import { ProjectsGridActions } from "shared/reducers/ProjectsGridSlice";
import { DesignerTab } from "shared/constants/designerTab";
import { HiddenContentText } from "shared/components/projectCard/components/HiddenContentText";
import { AlertBlock } from "shared/icons/AlertBlock";
import CheckSmallIcon from "shared/icons/CheckSmallIcon";

const Author = styled(FInter.SmallText)`
  color: ${Colors.geoWhite100};
  @media (max-width: 940px) {
  color: unset;
    div {
      font-weight: 400;
    }
  }
`

export interface BookCardProps extends Partial<IBookLight>/*, BookEvents*/ {
  isLoading?: boolean;
  width?: string;
  height?: string;
  onShouldReload?: (sender: any) => void;
  book?: Book;
  rank?: number;
  metaAlwaysVisible?: boolean;
  dataKey?: any;
  isSelectable?: boolean;
  selected?: boolean;
  alreadyProject?: boolean;
  linkPrefix?: string;
  isCanNotParticipate?: boolean;
  itemType?: string;
  channel?: Team<'Global'>;
  isChannel?: boolean;
  isCanNotBeDraft?: boolean;
  isSelectRightNeed?: boolean;
  isSortedByNotModerated?: boolean;
  withoutRepost?: boolean;
  isRepostHidden?: boolean,
  isBookmarkHidden?: boolean,
  isCanNotBeBookmark?: boolean;
  isParticipant?: boolean;
  onNotParticipate?: (projectId: string) => void;
  onBookMoveDrafts?: (identifier: string) => void
  onUpdate?: () => void;
  onSelect?: (projectId: string) => void;
  currentCompetitionId?: string;
  showCompetitionPoint?: boolean;
  showCompetitionJuryPoint?: boolean;
  showFirstCompetitionPoint?: boolean;
  entityType?: string
  isMobile?: boolean;
  linkTo?: string;
  linkTarget?: '_self' | '_blank';
}

const BookCard: FC<BookCardProps> = (props) => {
    const {
      isLoading,
      identifier,
      title,
      likes,
      author,
      coAuthors,
      isLiked,
      isSelectable,
      selected,
      alreadyProject,
      width,
      height,
      rank,
      onShouldReload,
      onSelect,
      metaAlwaysVisible,
      isCanNotBeDraft,
      withoutRepost,
      isSelectRightNeed,
      book,
      channel,
      linkPrefix,
      isCanNotParticipate,
      itemType,
      isChannel,
      isParticipant,
      onNotParticipate,
      onBookMoveDrafts,
      onUpdate,
      entityType,
      isRepostHidden,
      isBookmarkHidden,
      currentCompetitionId,
      showCompetitionPoint,
      showCompetitionJuryPoint,
      showFirstCompetitionPoint,
      isSortedByNotModerated,
      isMobile,
      linkTo,
      linkTarget,
    } = props;

    const isInCompetition = useMemo(() =>
      //@ts-ignore
      (Boolean(book && !book?.canEdit_PartCompetition)), [book])

    const history = useHistory();
    const profile = useProfile();
    const toasts = useToasts();
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string}>();
    const { t, i18n } = useTranslation();
    const isOnlines = AppTester.isOnlines();
    const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const [isBookmarked, setIsBookmarked] = useState<boolean>(false);

    const isFeedPage = useRouteMatch('/feed');

    const titleImages = getBookImages(book, i18n.language);

    //@ts-ignore
    const currentTeam = useSelector((state) => state?.organizations)?.team;

    const isFeedOwner = useMemo(
      () => isFeedPage || (entityType === DesignerTab.subscriptionsProjects && 
        id && (id === profile?.nickName || id === profile?.accountId))
    , [isFeedPage, entityType, id, profile])

    const handleToolTipToggleMove = (val: boolean) => {
      setTooltipOpen && setTooltipOpen(val);
    }

    const onDesignerSelected = useCallback((designerId: string): void => {
      history.push(CreateLinkTo.designer(designerId));
    }, [history]);

    const hidePrivateProject = useMemo(() => {
      return isChannel && !isParticipant && book?.status === 10
    } ,[isChannel, isParticipant, book])

    const onDraft = useCallback((bookId: string): void => {

      if (isInCompetition) {
        toasts.error(t('toasts.project_part_competition'));
        return;
      }
      
      if (onBookMoveDrafts) {
        onBookMoveDrafts(bookId)
        toasts.info(t("toasts.book_move_to_drafts_title", {
          title
        }));
        return
      }

      const prompt = window.confirm(t('book_edit.confirmation_msg.to_drafts'));

      if (!prompt) return

      BookAPI.UpdateStatus({ bookStatus: 0, bookId }).then(() => {
        onShouldReload?.(bookId);
        toasts.info(t("toasts.book_move_to_drafts_title", {
          title
        }));
      });
    }, [toasts, onShouldReload, title, isInCompetition]);

    const handleBookmarkClick = useCallback(() => {
      if (!book) return false;
      const method = isBookmarked ? 'RemoveBookmark' : 'Bookmark';
      BookmarkAPI[method]({ entityId: book.bookId, entityType: "project" })
        .then(() => {
          toasts.info(t(`book_edit.frames.book_bookmark_${method.toLowerCase()}`));
          setIsBookmarked(!isBookmarked);
          onUpdate && onUpdate();
          return;
        })
        .catch(() => {
          toasts.error(t('NEED_AUTHORIZATION'));
        })
      }, [t, book, toasts, isBookmarked]);

      useEffect(() => {
        if (!book) return
        //@ts-ignore
        setIsBookmarked(book.isBookmark);
      }, []);

    const isRejected = useMemo(() => BookDelegate.isRejectedBook(book), [book]);

    const backgroundStyles = useMemo(() => {
      if (hidePrivateProject) {
        return {
          background: 'linear-gradient( 180deg,rgba(0, 0, 0, 0),  rgba(24, 26, 28, 0.3), rgba(24, 26, 28, 1) )',
          backdropFilter: 'blur(25px)',
          marginTop: 0
        }
      }
      return undefined
    } , [hidePrivateProject])

    const backgroundText = useMemo(() => {
      if (hidePrivateProject) {
        return <HiddenContentText />
      }
      return undefined
    } , [hidePrivateProject])

    const onEdit = useCallback((bookId: string): void => {
      if (isInCompetition) {
        toasts.error(t('toasts.project_part_competition'));
        return;
      }
      history.push((linkPrefix ? linkPrefix : "") + CreateLinkTo.bookEdit(bookId))
    }, [history, linkPrefix]);

    const canBeDraft = useMemo(
      () => !isCanNotBeDraft && BookDelegate.canBeDraft(profile, props.book) && !BookDelegate.isPaid(props.book),
      [profile, props.book, isCanNotBeDraft]
    );

    const canEdit = useMemo(
      () => BookDelegate.canEdit(profile, props.book) && !BookDelegate.isPaid(props.book),
      [profile, props.book]
    );

  const competitionGPA = useMemo(() => {
    if (showCompetitionPoint && !isOnlines && currentCompetitionId && book) {
      if (showCompetitionJuryPoint) {
        //@ts-ignore
        return book.competitionGPA;
      }
      //@ts-ignore
      const points = book.competitonsGPA?.find(x => x.competitionId === currentCompetitionId);

      if (points) {
        return points.competitionGPA;
      }

      return undefined;
    }
    else if (book && showFirstCompetitionPoint) {
      //@ts-ignore
      return book.competitonsGPA?.[0]?.competitionGPA;
    }
    else {
      return undefined;
    }
  }, [showCompetitionPoint, showCompetitionJuryPoint, isOnlines, currentCompetitionId, book, showFirstCompetitionPoint]);
  const selectCheckIcon = useMemo(() => (
    <div>
      <CheckSmallIcon />
    </div>
  ), []);

  const selectRing = useMemo(() => {
    if (!isSortedByNotModerated) return undefined;
    return (
      <SelectRing className='selectRing' active={selected} ringPosition={RingPositions.left} onClick={(ev) => {
        if (onSelect) {
          ev.stopPropagation();
          ev.preventDefault();
          onSelect(identifier ?? '');
        }
      }}>
        {selected && selectCheckIcon}
      </SelectRing>
    );
  }, [identifier, isSortedByNotModerated, onSelect, selected, selectCheckIcon]);

  if(hidePrivateProject) {
    return (
      <BookCardLayout 
        book={book}
        isBook={book?.isBook}
        identifier={identifier}
        titleImages={titleImages}
        isLoading={isLoading}
        width={width}
        height={height}
        itemType={itemType}
        author={undefined}
        withoutGoToProject
        backgroundStyles={backgroundStyles}
        backgroundText={backgroundText}
        status={isRejected ? <AlertBlock /> : undefined}
        isRejected={isRejected}
        isMobile={isMobile}
      />
    )
  }

    return (
      <BookCardLayout
        book={book}
        linkParams={showCompetitionJuryPoint && currentCompetitionId ? `?currentCompetitionId=${currentCompetitionId}` : ''}
        identifier={identifier}
        titleImages={titleImages}
        isLoading={isLoading}
        isMobile={isMobile}
        linkTo={linkTo}
        linkTarget={linkTarget}
        // @ts-ignore
        isBook={book?.isBook}
        width={width}
        height={height}
        rank={rank}
        metaAlwaysVisible={metaAlwaysVisible}
        title={<ProjectCardTitle>{title}</ProjectCardTitle>}
        isMoreVisible={canBeDraft || canEdit || isSelectable}
        tooltipOpen={isTooltipOpen}
        toggleTooltip={handleToolTipToggleMove}
        linkPrefix={linkPrefix ? linkPrefix : undefined }
        itemType={itemType}
        isRepostHidden={isRepostHidden}
        isBookMarkHidden={isBookmarkHidden}
        isSelectRightNeed={isSelectRightNeed || isSortedByNotModerated}
        bookmarkButton={
          (!isSelectable && <BookmarkButton isBookmarked={isBookmarked} onClick={handleBookmarkClick}/>)
        }
        status={isRejected ? <AlertBlock /> : undefined}
        isRejected={isRejected}
        selectButton={
          isSortedByNotModerated
            ? selectRing
            : (alreadyProject || isSelectable) && (
              <>
              {alreadyProject ? (
              <SelectRing active={selected}>
                  {selectCheckIcon}
              </SelectRing>
            ) : isSelectable
              ? (
                <SelectRing active={selected} onClick={(ev) => {
                  if (onSelect) {
                    ev.stopPropagation();
                    ev.preventDefault();
                    onSelect(identifier ?? '');
                  }
                }}>
                  {selected && selectCheckIcon}
                </SelectRing>) : false}
          
            </>
            )
        }
        more={(
          (!isSelectable && (
            <BookCardTools
              isCanBeDraft={canBeDraft}
              isEditable={canEdit}
              identifier={identifier}
              book={book}
              onBookMoveDrafts={onDraft}
              onBookEdit={onEdit}
              isCanNotParticipate={isCanNotParticipate}
              onNotParticipate={onNotParticipate}
            />
          ))
        )}
        author={
          <Author>
            <ProjectDesignerName
              value={author ? [author].concat(coAuthors ?? []) : (coAuthors ?? [])}
              onSelected={onDesignerSelected}
              linkTarget={linkTarget === "_blank" ? "_blank" : undefined}
            />
          </Author>
        }
        repostButton={
          !isSelectable && !withoutRepost && profile && (
            <RepostButton
              project={book as unknown as Project} 
              iconOnPage 
              onUpdate={onShouldReload}
            />
          )
        }
        point={
          ((showFirstCompetitionPoint || showCompetitionPoint) && !isOnlines) && <PointBox points={competitionGPA}/>
        }
        likes={
          !isOnlines && (
            <LikesBox likes={likes ?? 0} isLiked={isLiked} isInteractive onClick={() => history.push(CreateLinkTo.project(identifier ?? ''))}/>
          )
        }
      />
    );
  }
;

export default memo(BookCard);
